<template>
  <div v-if="visible" class="my-overtime" :class="'my-overtime--' + tone">
    <div class="my-overtime__head">
      <span class="my-overtime__label">今月の時間外</span>
      <strong class="my-overtime__value">{{ actualText }}</strong>
      <span class="my-overtime__limit">／ 限度 {{ limitText }}</span>
    </div>

    <!--
      **棒の長さは、限度に対する割合。**数字だけだと
      「45時間のうち36時間」がどれくらいなのか、毎回頭で割ることになる。
    -->
    <div class="my-overtime__bar" role="img" :aria-label="ariaLabel">
      <span class="my-overtime__fill" :style="{ width: fillWidth }"></span>
      <span
        v-if="thresholdRatio !== null"
        class="my-overtime__mark"
        :style="{ left: thresholdLeft }"
      ></span>
    </div>

    <p class="my-overtime__note">{{ note }}</p>
    <p v-if="caution" class="my-overtime__caution">{{ caution }}</p>
  </div>
</template>

<script>
/**
 * 自分の今月の時間外が、限度のどこにいるか。
 *
 * <h2>なぜ本人に出すのか</h2>
 *
 * <p>法令の合図はどれも<b>事後</b>で、管理者向けの通知は月1回。
 * 月半ばに45時間を超えた人は3週間後に知らされる。
 * <b>その間に働き方を変えられるのは本人だけ。</b></p>
 *
 * <p>しかも本人に出すぶんには、氏名の扱いの問題が起きない
 * （自分のことを自分が見るだけ）。管理者向けの一覧が名前を出せず
 * 人数だけになっているのとは事情が違う。</p>
 *
 * <h2>煽らない</h2>
 *
 * <p><b>「危険」「違反」と書かない。</b>残業が多いことは、本人の
 * 落ち度とは限らない。事実（何時間で、限度が何時間か）を出して、
 * 判断は本人と会社に任せる。</p>
 */
export default {
  name: 'DashboardMyOvertimeGauge',

  props: {
    /** 今月の時間外（分）。 */
    overtimeMinutes: { type: Number, default: null },
    /** 36協定の月の限度時間（分）。 */
    limitMinutes: { type: Number, default: null },
    /** 会社が決めた「近づいた」の割合（%）。 */
    thresholdPercent: { type: Number, default: null },
    /**
     * 読み取れていない日があるか。
     *
     * **合計は小さいほうへずれる。**「まだ余裕がある」と見えるほうへ
     * 倒れるので、そのことを添える。
     */
    incomplete: { type: Boolean, default: false }
  },

  computed: {
    /**
     * 出すかどうか。
     *
     * **限度が分からなければ出さない。**36協定を登録していない会社で
     * 「45時間」と決め打ちすると、変形労働時間制の会社（42時間）で
     * 嘘になる。
     */
    visible () {
      return this.limitMinutes > 0 && this.overtimeMinutes !== null
    },

    ratio () {
      if (!this.visible) return 0
      return this.overtimeMinutes / this.limitMinutes
    },

    thresholdRatio () {
      if (!this.thresholdPercent) return null
      return this.thresholdPercent / 100
    },

    /** 棒の長さ。**100%で止める**——超えた人の棒が枠を突き抜けない。 */
    fillWidth () {
      return Math.min(100, Math.round(this.ratio * 1000) / 10) + '%'
    },

    thresholdLeft () {
      if (this.thresholdRatio === null) return '0%'
      return Math.min(100, Math.round(this.thresholdRatio * 1000) / 10) + '%'
    },

    /**
     * 色。**3段。**
     *
     * 予告の位置を決めていない会社では、超えたときだけ色が変わる。
     */
    tone () {
      if (this.ratio >= 1) return 'over'
      if (this.thresholdRatio !== null && this.ratio >= this.thresholdRatio) {
        return 'near'
      }
      return 'normal'
    },

    actualText () {
      return this.toHours(this.overtimeMinutes)
    },

    limitText () {
      return this.toHours(this.limitMinutes)
    },

    /**
     * 説明。
     *
     * **残りを出す。**「36時間」より「あと9時間」のほうが、
     * 今日どうするかを決めやすい。
     */
    note () {
      if (!this.visible) return ''
      const remain = this.limitMinutes - this.overtimeMinutes
      if (remain < 0) {
        return '1か月の限度時間を ' + this.toHours(-remain) + ' 超えています。'
          + '会社の36協定で決めた上限です。'
      }
      if (remain === 0) {
        return '1か月の限度時間ちょうどです。'
      }
      return '1か月の限度時間まで あと ' + this.toHours(remain) + '。'
    },

    /**
     * 読み取れていない日があるときの断り。
     *
     * <b>数字だけを黙って出さない。</b>合計が小さいほうへずれるので、
     * 「まだ余裕がある」と読まれる。
     */
    caution () {
      if (!this.visible || !this.incomplete) return ''
      return '勤務表に入力されていない日があるため、'
        + '実際にはこれより多い可能性があります。'
    },

    ariaLabel () {
      return '今月の時間外 ' + this.actualText
        + '、1か月の限度時間 ' + this.limitText
    }
  },

  methods: {
    /** 分を「◯時間◯分」に。**分を捨てない**——切り上げも切り捨てもしない。 */
    toHours (minutes) {
      const value = Math.max(0, Number(minutes) || 0)
      const h = Math.floor(value / 60)
      const m = value % 60
      if (m === 0) return h + '時間'
      return h + '時間' + String(m).padStart(2, '0') + '分'
    }
  }
}
</script>

<style scoped>
.my-overtime {
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d8e2ea;
  background: #f8fbfd;
}

.my-overtime__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.my-overtime__label { font-size: 12px; color: #5c7382; font-weight: 700; }
.my-overtime__value { font-size: 22px; color: #17384d; }
.my-overtime__limit { font-size: 12px; color: #6a7f8c; }

.my-overtime__bar {
  position: relative;
  height: 8px;
  margin: 8px 0 6px;
  border-radius: 999px;
  background: #e3ebf1;
  overflow: hidden;
}

.my-overtime__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #3a9fc4;
  transition: width .2s ease;
}

/*
  予告の位置。**棒の中に線を1本。**
  overflow:hidden の中なので、100%のときも枠から出ない。
*/
.my-overtime__mark {
  position: absolute;
  top: 0;
  width: 2px;
  height: 100%;
  background: #b07d2a;
}

.my-overtime__note { margin: 0; font-size: 12px; color: #5c7382; line-height: 1.5; }
.my-overtime__caution { margin: 4px 0 0; font-size: 12px; color: #8a6420; line-height: 1.5; }

/*
  **色は3段とも自分で決め切る。**
  親のカードの色に頼ると、置き場所を変えた日に読めなくなる。
*/
.my-overtime--near { border-color: #e0c08a; background: #fdf8ef; }
.my-overtime--near .my-overtime__fill { background: #df9841; }
.my-overtime--near .my-overtime__note { color: #8a6420; }

.my-overtime--over { border-color: #e0a0a6; background: #fdf3f4; }
.my-overtime--over .my-overtime__fill { background: #d95e6a; }
.my-overtime--over .my-overtime__note { color: #a1323f; font-weight: 700; }
</style>
